import { create } from 'zustand';
import type {
  AnnotationResponse,
  ApiClient,
  CreateAnnotationInput,
  UpdateAnnotationInput,
} from '../../../lib/api';
import { useTeamStore } from '../../prompts/store/teams';
import type {
  CloudAnnotation,
  CreateCloudAnnotationInput,
  UpdateCloudAnnotationInput,
} from '../types/cloud';

const MAX_MESSAGE_TEXT_LENGTH = 500;

export interface CloudAnnotationsState {
  annotations: CloudAnnotation[];
  isLoading: boolean;
  error: string | null;
}

export interface CloudAnnotationsActions {
  setApiClient: (client: ApiClient | null) => void;
  fetchAnnotations: () => Promise<void>;
  createAnnotation: (input: CreateCloudAnnotationInput) => Promise<CloudAnnotation | null>;
  deleteAnnotation: (id: string) => Promise<boolean>;
  updateNote: (id: string, note: string) => Promise<CloudAnnotation | null>;
  clearError: () => void;
  reset: () => void;
}

export type CloudAnnotationsStore = CloudAnnotationsState & CloudAnnotationsActions;

const initialState: CloudAnnotationsState = {
  annotations: [],
  isLoading: false,
  error: null,
};

let apiClient: ApiClient | null = null;

export function setCloudAnnotationsApiClient(client: ApiClient | null): void {
  apiClient = client;
}

function getAnnotationsResultData(
  result: { annotations: AnnotationResponse[] } | undefined,
): AnnotationResponse[] | null {
  if (!result || !Array.isArray(result.annotations)) {
    return null;
  }

  return result.annotations;
}

export const useCloudAnnotationsStore = create<CloudAnnotationsStore>((set, get) => ({
  ...initialState,

  setApiClient: (client) => {
    setCloudAnnotationsApiClient(client);
  },

  fetchAnnotations: async () => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return;
    }

    set({ isLoading: true, error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.getTeamAnnotations(activeWorkspace.teamId)
      : await apiClient.getAnnotations();

    if (result.success) {
      const annotations = getAnnotationsResultData(result.data);
      if (!annotations) {
        set({ error: 'Unexpected response format from API', isLoading: false });
        return;
      }

      set({ annotations, isLoading: false });
      return;
    }

    set({
      error: result.error.message ?? result.error.error,
      isLoading: false,
    });
  },

  createAnnotation: async (input) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    set({ error: null });

    const sanitizedInput: CreateCloudAnnotationInput = {
      ...input,
      messageText: input.messageText.slice(0, MAX_MESSAGE_TEXT_LENGTH),
    };

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.createTeamAnnotation(
        activeWorkspace.teamId,
        sanitizedInput as CreateAnnotationInput,
      )
      : await apiClient.createAnnotation(sanitizedInput as CreateAnnotationInput);

    if (result.success) {
      const annotation = result.data.annotation;
      set((state) => ({
        annotations: [...state.annotations, annotation],
      }));
      return annotation;
    }

    set({ error: result.error.message ?? result.error.error });
    return null;
  },

  deleteAnnotation: async (id) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return false;
    }

    const { annotations } = get();
    const originalAnnotations = annotations;

    set((state) => ({
      annotations: state.annotations.filter((annotation) => annotation.id !== id),
      error: null,
    }));

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.deleteTeamAnnotation(activeWorkspace.teamId, id)
      : await apiClient.deleteAnnotation(id);

    if (result.success) {
      return true;
    }

    set({
      annotations: originalAnnotations,
      error: result.error.message ?? result.error.error,
    });
    return false;
  },

  updateNote: async (id, note) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    const { annotations } = get();
    const originalAnnotation = annotations.find((annotation) => annotation.id === id);

    if (!originalAnnotation) {
      set({ error: 'Annotation not found' });
      return null;
    }

    const optimisticAnnotation: CloudAnnotation = {
      ...originalAnnotation,
      note,
      updatedAt: new Date().toISOString(),
    };

    set((state) => ({
      annotations: state.annotations.map((annotation) =>
        annotation.id === id ? optimisticAnnotation : annotation,
      ),
      error: null,
    }));

    const payload: UpdateCloudAnnotationInput = { note };
    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.updateTeamAnnotation(
        activeWorkspace.teamId,
        id,
        payload as UpdateAnnotationInput,
      )
      : await apiClient.updateAnnotation(id, payload as UpdateAnnotationInput);

    if (result.success) {
      const updatedAnnotation = result.data.annotation;
      set((state) => ({
        annotations: state.annotations.map((annotation) =>
          annotation.id === id ? updatedAnnotation : annotation,
        ),
      }));
      return updatedAnnotation;
    }

    set((state) => ({
      annotations: state.annotations.map((annotation) =>
        annotation.id === id ? originalAnnotation : annotation,
      ),
      error: result.error.message ?? result.error.error,
    }));
    return null;
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));
